// Collapsible sections start closed and remember what the player opened, on this device, across sessions.
// One map in localStorage ("valor-web:open") holds only the ids that are open; closing a section removes its id.
// Ids are "<screen>.<section>" (Empire city rows add the world and city id), so every collapsible keeps its own state.
import { create } from "zustand";

const KEY = "valor-web:open";

function load(): Record<string, true> {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || "{}") as Record<string, unknown>;
    return Object.fromEntries(Object.entries(raw).filter(([, v]) => v === true)) as Record<string, true>;
  } catch {
    return {};
  }
}

interface OpenState {
  open: Record<string, true>;
  set: (id: string, on: boolean) => void;
}

export const useOpenState = create<OpenState>((set, get) => ({
  open: load(),
  set: (id, on) => {
    const next = { ...get().open };
    if (on) next[id] = true;
    else delete next[id];
    set({ open: next });
    try {
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      /* private mode: the choice lasts for this visit */
    }
  },
}));

/** A remembered collapsible: closed until the player opens it, then open until they close it — across sessions. */
export function useRememberedOpen(id: string): [open: boolean, toggle: () => void] {
  const open = useOpenState((s) => !!s.open[id]);
  const set = useOpenState((s) => s.set);
  return [open, () => set(id, !open)];
}

/** A family of remembered collapsibles under one prefix (e.g. one per city): `is(k)` / `toggle(k)`. */
export function useRememberedOpenSet(prefix: string) {
  const open = useOpenState((s) => s.open);
  const set = useOpenState((s) => s.set);
  const id = (k: string | number) => `${prefix}.${k}`;
  return {
    is: (k: string | number) => !!open[id(k)],
    toggle: (k: string | number) => set(id(k), !open[id(k)]),
  };
}
